import { IHisInformationApplicable, instanceOfIHisInformationApplicable } from "../../document/items";
import { HisInformationItem, HisInformation, HisInformationForceUpdateParameter } from "..";
import { IHisItemAction } from ".";
import { IDocumentEditable } from "../../../components/document/editor";
import { EditPhaseKinds } from "../../document";
import { HisInformationProgressMaster, HisInformationUpdateEveryTimeMaster } from "../master";
import { User } from "../../user";
import { Department } from "../../department";
import { MischievousHisInformationApplicator } from "../../../components/document/editor/helpers";
import { HisItemParameter, InformationKinds } from "../behavior";

export class HisItemProgressAction implements IHisItemAction {
  applicableItems: IHisInformationApplicable[][];
  informationItems: HisInformationItem[];
  managerIds: string[];
  hospitalizationItems: HisInformationItem[];
  diseaseItems: HisInformationItem[];

  constructor(private _document: IDocumentEditable, info: HisInformation, progress: EditPhaseKinds, user: User, department: Department, orderUser: User, attendingUser: User, isFirstEdit: boolean, hostName: string, forceUpdateParam: HisInformationForceUpdateParameter) {
    this.applicableItems = this.getApplicableItems(_document, progress, isFirstEdit, forceUpdateParam);
    this.informationItems = this.getInformationItems(info, user, department, orderUser, attendingUser, hostName);
    this.managerIds = info.managerIds;

    // wfmst.WorkflowSettingsのHisInfoでWF→通常のHIS項目に値を反映させる機能を有効にした場合、ここに複数の項目が順番通りに格納されてくる
    this.hospitalizationItems = info.hospitalizationItems;
    this.diseaseItems = info.diseaseItems;
  }

  private getApplicableItems(document: IDocumentEditable, progress: EditPhaseKinds, isFirstEdit: boolean, forceUpdateParam: HisInformationForceUpdateParameter): IHisInformationApplicable[][] {
    const targetItemSetItems = document.itemSets.map(itemSet => itemSet.items);
    if (forceUpdateParam && forceUpdateParam.shouldUpdateExtraSectionOnly) {
      // itemSetId=0のitemsは続紙では無いので対象外とする
      // shift()するとitemSetIdがずれてしまうので注意
      targetItemSetItems[0] = [];
    }
    return targetItemSetItems.map(items => items
      .filter(instanceOfIHisInformationApplicable)
      .filter(item => item.hisItemParameter != null)
      .filter(item => this.shouldAppliedProgress(item, progress, isFirstEdit, forceUpdateParam))
    );
  }

  private shouldAppliedProgress(item: IHisInformationApplicable, progress: EditPhaseKinds, isFirstEdit: boolean, forceUpdateParam: HisInformationForceUpdateParameter): boolean {
    const applicator = new MischievousHisInformationApplicator(this._document, null, false);
    if (applicator.shouldRollbackValue(item.hisItemParameter, progress)) { return true; }  // 一時保存時に前回値を表示するため設定する

    if (!item.isEmpty && !this.isUpdateEveryTimeHisItem(item) && !this.isForceUpdateTarget(item.hisItemParameter, forceUpdateParam)) { return false; } // 暫定対応として毎回更新する項目以外は値が空のときだけ値を設定する。 TODO: HIS項目の仕様の検討

    const isFirst = isFirstEdit || progress === EditPhaseKinds.EditStartFirst;
    if (!isFirst && item.shouldInputFree) {
      // 基本的にはフリー入力状態のオブジェクトには値を反映しない。例外として初回編集時は反映させる
      // progressがEditPhaseKinds.EditStartFirstかどうかだけで判定すると、ワークフローの依頼書のHIS項目が入ってこなくなってしまう
      return false;
    }

    const progresses = HisInformationProgressMaster.getProgresses(item.hisItemParameter.updateTimingKind);
    if (progresses == null) { return false; }
    
    const contains = progresses.indexOf(progress) >= 0;
    return contains;
  }

  private isForceUpdateTarget(hisItemParameter: HisItemParameter, forceUpdateParam: HisInformationForceUpdateParameter): boolean {
    if (!hisItemParameter || !forceUpdateParam) { return false; }
    return forceUpdateParam.shouldUpdateAllInformation || (forceUpdateParam.shouldUpdatePatientInformation && (hisItemParameter.informationKind === InformationKinds.Patient));
  }

  private getInformationItems(info: HisInformation, user: User, department: Department, orderUser: User, attendingUser: User, hostName: string): HisInformationItem[] {
    const userItems = this.createUserItems(user).filter(x => info.items.every(y => y.name !== x.name));
    const departmentItems = this.createDepartmentItems(department).filter(x => info.items.every(y => y.name !== x.name));
    const orderDoctorItems = this.createOrderDoctorItems(orderUser).filter(x => info.items.every(y => y.name !== x.name));
    const attendingDoctorItems = this.createAttendingDoctorItems(attendingUser).filter(x => info.items.every(y => y.name !== x.name));
    const hostNameItems = this.createHostNameItems(hostName).filter(x => info.items.every(y => y.name !== x.name));
    const items = info.items
      .concat(userItems)
      .concat(departmentItems)
      .concat(orderDoctorItems)
      .concat(attendingDoctorItems)
      .concat(hostNameItems);

    // 職種に応じて自動的に反映する機能を有効にしている場合はここで除去しておく必要がある
    return this._document.source.attributes.behavior.shouldFillDoctorAutomaticallyOnEditStart
      ? items.filter(i => i.name !== "DOCTOR")
      : items;
  }

  private createUserItems(user?: User): HisInformationItem[] {
    if (user == null) { return []; }

    return [
      new HisInformationItem("DOCTOR", user.name),
      new HisInformationItem("CREATE_USER", user.name),
      new HisInformationItem("LAST_UPDATE_USER", user.name),
      new HisInformationItem("USER_ACCOUNT", user.id),
      new HisInformationItem("WORKFLOW_MANAGE_OPERATOR", user.name)
    ];
  }

  private createDepartmentItems(department?: Department): HisInformationItem[] {
    if (department == null) { return []; }

    return [
      new HisInformationItem("SRYKA_NAME", department.name),
      new HisInformationItem("SRYKA_SELECT", department.name),
    ];
  }

  private createOrderDoctorItems(orderUser?: User): HisInformationItem[] {
    if (orderUser == null) { return []; }

    return [
      new HisInformationItem("ORDER_DOCTOR", orderUser.name),
    ];
  }

  private createAttendingDoctorItems(attendingUser?: User): HisInformationItem[] {
    if (attendingUser == null) { return []; }

    return [
      new HisInformationItem("ATTENDING_DOCTOR", attendingUser.name),
    ];
  }

  private createHostNameItems(hostName: string): HisInformationItem[] {
    return [
      new HisInformationItem("HOST_NAME", hostName ? hostName : ""),
    ];
  }

  private isUpdateEveryTimeHisItem(item: IHisInformationApplicable): boolean {
    return HisInformationUpdateEveryTimeMaster.isUpdateEveryTime(item.hisItemParameter.key);
  }
}
